type UnknownRecord = Record<string, unknown>;
type ApiErrorStatus = number | string;
type IssuePathSegment = string | number;

export const isObjectRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

type ApiValidationIssue = UnknownRecord & {
  field?: unknown;
  path?: unknown;
};

const isApiValidationIssue = (value: unknown): value is ApiValidationIssue =>
  isObjectRecord(value);

const getNonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;

  const trimmedValue = value.trim();
  return trimmedValue ? trimmedValue : undefined;
};

const isJsonLikeString = (value: string): boolean => {
  const trimmedValue = value.trim();
  return trimmedValue.startsWith("{") || trimmedValue.startsWith("[");
};

const parseJsonValue = (value: string): unknown | undefined => {
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return undefined;
  }
};

const getMessageFromRecord = (value: UnknownRecord): string | undefined => {
  const nestedError = value.error;
  const nestedErrorMessage = isObjectRecord(nestedError)
    ? getNonEmptyString(nestedError.message) || getNonEmptyString(nestedError.errorMessage)
    : getNonEmptyString(nestedError);

  return (
    nestedErrorMessage ||
    getNonEmptyString(value.message) ||
    getNonEmptyString(value.errorMessage)
  );
};

const getMessageFromValue = (value: unknown): string | undefined => {
  if (value instanceof Error) return getNonEmptyString(value.message);

  if (typeof value === "string") {
    const parsedValue = parseJsonValue(value);
    if (parsedValue !== undefined) return getMessageFromValue(parsedValue);

    return isJsonLikeString(value) ? undefined : getNonEmptyString(value);
  }

  if (!isObjectRecord(value)) return undefined;

  return getMessageFromRecord(value);
};

export const getApiErrorPayload = (error: unknown): unknown => {
  if (isObjectRecord(error) && "data" in error && error.data !== undefined) {
    return error.data;
  }

  return error;
};

export const getApiErrorStatus = (error: unknown): ApiErrorStatus | undefined => {
  if (!isObjectRecord(error)) return undefined;

  const { status } = error;
  return typeof status === "number" || typeof status === "string" ? status : undefined;
};

export const getApiErrorCode = (error: unknown): string | undefined => {
  const payload = getApiErrorPayload(error);
  if (!isObjectRecord(payload)) return undefined;
  if (typeof payload.code === "string") return payload.code;
  if (isObjectRecord(payload.error) && typeof payload.error.code === "string") {
    return payload.error.code;
  }
  return undefined;
};

const INTERNAL_ERROR_MESSAGE_PATTERN =
  /failed query|invalid input syntax|syntax error|select\s+.+\s+from\s+/i;

export const sanitizeApiErrorMessage = (message: string): string => {
  if (INTERNAL_ERROR_MESSAGE_PATTERN.test(message)) {
    return "No se pudieron cargar los eventos. Revisá los filtros seleccionados o intentá de nuevo en unos minutos.";
  }
  return message;
};

export const getApiErrorMessage = (error: unknown): string | undefined => {
  const payload = getApiErrorPayload(error);
  const payloadMessage = getMessageFromValue(payload);
  if (payloadMessage) return sanitizeApiErrorMessage(payloadMessage);

  if (payload === error) return undefined;

  const errorMessage = getMessageFromValue(error);
  return errorMessage ? sanitizeApiErrorMessage(errorMessage) : undefined;
};

const ADMIN_HTTP_ERROR_MESSAGES: Partial<Record<number, string>> = {
  404: "No se encontró el servicio de reglas. Verificá que la API esté disponible o contactá al equipo técnico.",
  500: "Ocurrió un error en el servidor. Intentá de nuevo en unos minutos o contactá al equipo técnico.",
  502: "El servicio no está disponible en este momento. Intentá de nuevo en unos minutos.",
  503: "El servicio no está disponible en este momento. Intentá de nuevo en unos minutos.",
};

const ADMIN_NETWORK_ERROR_MESSAGE =
  "No se pudo conectar con el servidor. Verificá tu conexión e intentá de nuevo en unos minutos. Si el problema continúa, contactá al equipo técnico.";

const NETWORK_ERROR_MESSAGE_PATTERN =
  /failed to fetch|networkerror|network error|load failed|fetch failed/i;

const isTechnicalNotFoundMessage = (message: string): boolean =>
  /not\s*found|404/i.test(message);

const isTechnicalNetworkMessage = (message: string): boolean =>
  NETWORK_ERROR_MESSAGE_PATTERN.test(message);

const getRawErrorText = (error: unknown): string | undefined => {
  if (!isObjectRecord(error)) return undefined;

  const directError = getNonEmptyString(error.error);
  if (directError) return directError;

  return getApiErrorMessage(error);
};

export const isApiNetworkError = (error: unknown): boolean => {
  const status = getApiErrorStatus(error);
  if (status === "FETCH_ERROR" || status === "TIMEOUT_ERROR") {
    return true;
  }

  const rawErrorText = getRawErrorText(error);
  return rawErrorText ? isTechnicalNetworkMessage(rawErrorText) : false;
};

export const getAdminFacingApiErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  if (isApiNetworkError(error)) {
    return ADMIN_NETWORK_ERROR_MESSAGE;
  }

  const status = getApiErrorStatus(error);
  const numericStatus =
    typeof status === "number" ? status : Number.parseInt(String(status ?? ""), 10);

  if (Number.isFinite(numericStatus) && ADMIN_HTTP_ERROR_MESSAGES[numericStatus]) {
    return ADMIN_HTTP_ERROR_MESSAGES[numericStatus] as string;
  }

  const rawMessage = getApiErrorMessage(error);
  if (rawMessage && isTechnicalNetworkMessage(rawMessage)) {
    return ADMIN_NETWORK_ERROR_MESSAGE;
  }

  if (rawMessage && !isTechnicalNotFoundMessage(rawMessage)) {
    return rawMessage;
  }

  if (numericStatus === 404 || isTechnicalNotFoundMessage(rawMessage ?? "")) {
    return ADMIN_HTTP_ERROR_MESSAGES[404] as string;
  }

  return fallback;
};

const getIssueList = (value: unknown): ApiValidationIssue[] | undefined => {
  if (!Array.isArray(value)) return undefined;

  const issues = value.filter(isApiValidationIssue);
  return issues.length > 0 ? issues : undefined;
};

const getIssuesFromValue = (value: unknown): ApiValidationIssue[] | undefined => {
  if (typeof value === "string") {
    const parsedValue = parseJsonValue(value);
    return parsedValue === undefined ? undefined : getIssuesFromValue(parsedValue);
  }

  const directIssues = getIssueList(value);
  if (directIssues) return directIssues;

  if (!isObjectRecord(value)) return undefined;

  const issues = getIssueList(value.issues) || getIssueList(value.details);
  if (issues) return issues;

  if (isObjectRecord(value.error)) return getIssuesFromValue(value.error);

  return undefined;
};

export const getApiValidationIssues = (error: unknown): ApiValidationIssue[] | undefined => {
  const payload = getApiErrorPayload(error);
  return getIssuesFromValue(payload) || getIssuesFromValue(error);
};

const formatPathSegment = (value: unknown): string | undefined => {
  if (typeof value === "number") return String(value);
  return getNonEmptyString(value);
};

const getIssueFieldName = (issue: ApiValidationIssue): string | undefined => {
  const field = formatPathSegment(issue.field);
  if (field) return field;

  const { path } = issue;
  if (Array.isArray(path)) {
    const segments = path
      .map((segment): IssuePathSegment | undefined => formatPathSegment(segment))
      .filter((segment): segment is IssuePathSegment => segment !== undefined);

    return segments.length > 0 ? segments.join(".") : undefined;
  }

  return formatPathSegment(path);
};

export const getApiValidationIssueFields = (error: unknown): string[] => {
  const issues = getApiValidationIssues(error) || [];
  const fieldNames = new Set<string>();

  issues.forEach((issue) => {
    const fieldName = getIssueFieldName(issue);
    if (fieldName) fieldNames.add(fieldName);
  });

  return Array.from(fieldNames);
};

export const hasApiValidationIssues = (error: unknown): boolean =>
  Boolean(getApiValidationIssues(error)?.length);
